import { API_BASE_URL } from "../lib/app-config.js";
import { clearSessionCookie } from "../lib/auth.js";
import type {
  Annotation,
  DiaryListItem,
  DiaryWithPages,
  Language,
  PageData,
  PageTranslation,
} from "./types.js";

let indexPromise: Promise<DiaryListItem[]> | null = null;
const diaryPromises = new Map<string, Promise<DiaryWithPages>>();
const pagePromises = new Map<string, Promise<PageData>>();

function redirectToLogin(): void {
  if (typeof window === "undefined") return;
  clearSessionCookie();
  const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.href = `/login?return_to=${returnTo}`;
}

async function fetchJson(path: string): Promise<unknown> {
  const url = `${API_BASE_URL}${path}`;
  const res = await fetch(url, { credentials: "include" });
  if (res.status === 401 || res.status === 403) {
    redirectToLogin();
    throw new Error("Authentication required");
  }
  if (!res.ok) throw new Error(`GET ${url} failed: ${res.status} ${res.statusText}`);
  const contentType = res.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    redirectToLogin();
    throw new Error("Authentication required (got non-JSON response)");
  }
  return res.json();
}

export function loadIndex(): Promise<DiaryListItem[]> {
  if (!indexPromise) {
    indexPromise = fetchJson("/api/diaries").then(
      (raw) => (raw as { diaries: DiaryListItem[] }).diaries,
    );
  }
  return indexPromise;
}

export function loadDiary(slug: string): Promise<DiaryWithPages> {
  let p = diaryPromises.get(slug);
  if (!p) {
    p = fetchJson(`/api/diary/${slug}`).then((raw) => raw as DiaryWithPages);
    diaryPromises.set(slug, p);
  }
  return p;
}

export function loadPage(slug: string, pageNumber: number): Promise<PageData> {
  const key = `${slug}/${pageNumber}`;
  let p = pagePromises.get(key);
  if (!p) {
    p = fetchJson(`/api/diary/${slug}/page/${pageNumber}`).then((raw) => raw as PageData);
    pagePromises.set(key, p);
  }
  return p;
}

export function transcriptionAnnotations(page: PageData): Annotation[] {
  return page.annotations.filter((a) => a.target === "transcription");
}

export function translationFor(page: PageData, lang: Language): PageTranslation | undefined {
  return page.translations.find((t) => t.targetLanguage === lang);
}

export function translationAnnotations(page: PageData, lang: Language): Annotation[] {
  const target = `translation:${lang}`;
  return page.annotations.filter((a) => a.target === target);
}

export interface CreateAnnotationInput {
  target: string;
  startOffset: number;
  endOffset: number;
  type: string;
  sic: string;
  corr: string;
  certainty?: "high" | "medium" | "low" | null;
  note?: string | null;
}

export async function createAnnotation(
  pageId: string,
  input: CreateAnnotationInput,
): Promise<Annotation> {
  const res = await fetch(`${API_BASE_URL}/api/admin/page/${pageId}/annotation`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  if (res.status === 401 || res.status === 403) {
    redirectToLogin();
    throw new Error("Authentication required");
  }
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`POST annotation failed: ${res.status} ${body}`);
  }
  const json = (await res.json()) as { annotation: Annotation };
  // Invalidate the page cache so subsequent loadPage() re-fetches.
  pagePromises.clear();
  return json.annotation;
}

export function invalidatePageCache(): void {
  pagePromises.clear();
}

export async function deleteAnnotation(pageId: string, annotationId: string): Promise<void> {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/page/${pageId}/annotation/${annotationId}`,
    { method: "DELETE", credentials: "include" },
  );
  if (res.status === 401 || res.status === 403) {
    redirectToLogin();
    throw new Error("Authentication required");
  }
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`DELETE annotation failed: ${res.status} ${body}`);
  }
  pagePromises.clear();
}

async function patchJson(path: string, body: unknown): Promise<unknown> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (res.status === 401 || res.status === 403) {
    redirectToLogin();
    throw new Error("Authentication required");
  }
  if (!res.ok) {
    const errBody = await res.text();
    throw new Error(`PATCH ${path} failed: ${res.status} ${errBody}`);
  }
  pagePromises.clear();
  diaryPromises.clear();
  return res.json();
}

export function updateTranscription(pageId: string, text: string): Promise<unknown> {
  return patchJson(`/api/admin/page/${pageId}/transcription`, { text });
}

export function updateTranslation(
  pageId: string,
  lang: Language,
  text: string | null,
): Promise<unknown> {
  return patchJson(`/api/admin/page/${pageId}/translation/${lang}`, { text });
}

export function updatePageMetadata(
  pageId: string,
  fields: { authorLabel?: string | null },
): Promise<unknown> {
  return patchJson(`/api/admin/page/${pageId}/metadata`, fields);
}
